import React, {memo, useCallback, useEffect, useRef, useState} from "react";
import {NavigationProps} from "../../utils/navigationProps";
import {usePageTitle} from "../../hooks/usePageTitle";
import {RootState} from "../../store/rootReducer";
import {useDispatch, useSelector} from "react-redux";
import {styled} from "styled-components";
import {Link} from "@reach/router";
import {SideMenu} from "../../review/sideMenu/sideMenu";
import {uiActions} from "../../ui/ui.slice";
import {ThemeProps} from "../../styles/theme";
import {useTheme} from "../../styles/ThemeContext";
import {quickAddActions} from "./quickAdd.slice";
import {QuickAddCardRef, QuickAddItemDto, QuickAddStatus} from "../../common";
import {CardMarkupViewer} from "../../markup/cardMarkupViewer/cardMarkupViewer";
import {CardMarkupViewerState} from "../../markup/cardMarkupViewer/cardMarkupViewer.interface";

const selector = (state: RootState) => ({
    items: state.quickAdd.items,
    inputText: state.quickAdd.inputText,
    isProcessing: state.quickAdd.isProcessing,
    expandedIds: state.quickAdd.expandedIds,
});

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const ActionBar = styled.div<ThemeProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 50px;
    padding: 0 20px;
    background-color: ${props => props.theme.primaryBackground};
    border-bottom: 1px solid ${props => props.theme.primaryBorder};
`;

const Icon = styled.svg`
    width: 24px;
    height: 24px;
    fill: ${props => props.theme.inputText};
    cursor: pointer;
`;

const Content = styled.div<ThemeProps>`
    flex-grow: 1;
    overflow-y: auto;
    padding: 20px;
    box-sizing: border-box;
    color: ${props => props.theme.inputText};
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const InputRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    width: 100%;
    max-width: 700px;
    margin-bottom: 20px;
`;

const TextInput = styled.input<ThemeProps>`
    flex-grow: 1;
    padding: 10px 12px;
    font-size: 16px;
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 4px;
    background-color: ${props => props.theme.secondaryBackground};
    color: ${props => props.theme.inputText};
`;

const Button = styled.button<ThemeProps>`
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    border: none;
    border-radius: 4px;
    background-color: ${props => props.theme.primaryColor};
    color: white;
    cursor: pointer;

    &:hover {
        background-color: ${props => props.theme.accentColorDark};
    }

    &:disabled {
        background-color: ${props => props.theme.neutralButtonBackground};
        color: ${props => props.theme.secondaryText};
        cursor: not-allowed;
    }
`;

const QueueHeader = styled.div`
    width: 100%;
    max-width: 700px;
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
`;

const ClearButton = styled.button<ThemeProps>`
    padding: 6px 14px;
    font-size: 13px;
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 4px;
    background-color: transparent;
    color: ${props => props.theme.inputText};
    cursor: pointer;

    &:hover {
        background-color: ${props => props.theme.neutralButtonBackground};
    }
`;

const Queue = styled.div`
    width: 100%;
    max-width: 700px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

interface ItemRowProps extends ThemeProps {
    $processing: boolean;
}

const ItemRow = styled.div<ItemRowProps>`
    border: 1px solid ${props => props.$processing ? props.theme.accentColor : props.theme.primaryBorder};
    box-shadow: ${props => props.$processing ? `0 0 0 1px ${props.theme.accentColor}` : 'none'};
    border-radius: 8px;
    background-color: ${props => props.theme.secondaryBackground};
    padding: 12px 16px;
    box-sizing: border-box;
`;

const ItemHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
`;

const ItemText = styled.div<ThemeProps>`
    flex-grow: 1;
    font-size: 16px;
    font-weight: 500;
    color: ${props => props.theme.primaryText};
`;

interface StatusProps extends ThemeProps {
    $status: QuickAddStatus;
}

function statusColor(props: StatusProps): string {
    switch (props.$status) {
        case 'done': return props.theme.successColor;
        case 'error': return props.theme.dangerColor;
        case 'processing': return props.theme.accentColor;
        case 'duplicate': return props.theme.primaryColor;
        default: return props.theme.secondaryText;
    }
}

const StatusBadge = styled.span<StatusProps>`
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${statusColor};
`;

const LinkButton = styled.span<ThemeProps>`
    font-size: 13px;
    color: ${props => props.theme.accentColor};
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const CardActionIcon = styled.svg<ThemeProps>`
    width: 18px;
    height: 18px;
    cursor: pointer;
    fill: ${props => props.theme.accentColor};

    &:hover {
        opacity: 0.7;
    }
`;

const DangerActionIcon = styled(CardActionIcon)`
    margin-left: auto;
    fill: ${props => props.theme.dangerColor};
`;

const ErrorText = styled.div<ThemeProps>`
    margin-top: 8px;
    font-size: 13px;
    color: ${props => props.theme.dangerColor};
`;

const ExpandedArea = styled.div<ThemeProps>`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Toggle = styled.div<ThemeProps>`
    display: flex;
    align-self: flex-start;
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 6px;
    overflow: hidden;
`;

interface ToggleButtonProps extends ThemeProps {
    $active: boolean;
}

const ToggleButton = styled.button<ToggleButtonProps>`
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    background-color: ${props => props.$active ? props.theme.primaryColor : props.theme.secondaryBackground};
    color: ${props => props.$active ? 'white' : props.theme.inputText};

    &:not(:last-child) {
        border-right: 1px solid ${props => props.theme.primaryBorder};
    }
`;

const GeneratedCard = styled.div<ThemeProps>`
    border: 1px solid ${props => props.theme.primaryBorder};
    border-radius: 6px;
    padding: 16px;
    background-color: ${props => props.theme.primaryBackground};
`;

const GeneratedCardTitle = styled.div<ThemeProps>`
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 10px;
    color: ${props => props.theme.secondaryText};
    display: flex;
    gap: 12px;
    align-items: center;
`;

const EmptyCardNotice = styled.div<ThemeProps>`
    border: 1px dashed ${props => props.theme.primaryBorder};
    border-radius: 6px;
    padding: 16px;
    font-size: 13px;
    color: ${props => props.theme.secondaryText};
`;

const statusLabel: Record<QuickAddStatus, string> = {
    pending: 'В очереди',
    processing: 'Генерация…',
    done: 'Готово',
    error: 'Ошибка',
    duplicate: 'Дубликат',
};

const DuplicateNotice = styled.div<ThemeProps>`
    margin-top: 8px;
    font-size: 13px;
    color: ${props => props.theme.secondaryText};
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

const DuplicateLink = styled(Link)<ThemeProps>`
    color: ${props => props.theme.accentColor};

    &:hover {
        text-decoration: underline;
    }
`;

const GeneratedCardView = memo((props: { title: string; card: QuickAddCardRef; onDelete: () => void }) => {
    const { theme } = useTheme();
    return (
        <GeneratedCard theme={theme}>
            <GeneratedCardTitle theme={theme}>
                <span>{props.title}</span>
                <Link to={`/card/${props.card.cardId}`} aria-label="Открыть" title="Открыть">
                    <CardActionIcon theme={theme} viewBox="0 0 24 24">
                        <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                    </CardActionIcon>
                </Link>
                <Link to={`/edit/${props.card.cardId}`} aria-label="Редактировать" title="Редактировать">
                    <CardActionIcon theme={theme} viewBox="0 0 24 24">
                        <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
                    </CardActionIcon>
                </Link>
                <DangerActionIcon theme={theme} viewBox="0 0 24 24" role="button" aria-label="Удалить" onClick={props.onDelete}>
                    <title>Удалить</title>
                    <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                </DangerActionIcon>
            </GeneratedCardTitle>
            <CardMarkupViewer
                markup={props.card.markup}
                state={CardMarkupViewerState.Full}
                showSeparators={true}
            />
        </GeneratedCard>
    );
});
GeneratedCardView.displayName = 'GeneratedCardView';

const QuickAddItemRow = memo((props: { item: QuickAddItemDto; expanded: boolean }) => {
    const { theme } = useTheme();
    const dispatch = useDispatch();
    const { item, expanded } = props;
    const [direction, setDirection] = useState<'normal' | 'inverted'>('normal');

    const showNormal = useCallback(() => setDirection('normal'), []);
    const showInverted = useCallback(() => setDirection('inverted'), []);

    const toggleHandler = useCallback(() => {
        dispatch(quickAddActions.toggleExpand(item.id));
    }, [dispatch, item.id]);

    const retryHandler = useCallback(() => {
        dispatch(quickAddActions.retryItem(item.id));
    }, [dispatch, item.id]);

    const deleteHandler = useCallback(() => {
        dispatch(quickAddActions.deleteItem(item.id));
    }, [dispatch, item.id]);

    const deleteNormalCardHandler = useCallback(() => {
        if (item.normalCard !== undefined && window.confirm('Удалить прямую карточку? Это физическое удаление.')) {
            dispatch(quickAddActions.deleteGeneratedCard({itemId: item.id, direction: 'normal', cardId: item.normalCard.cardId}));
        }
    }, [dispatch, item.id, item.normalCard]);

    const deleteInvertedCardHandler = useCallback(() => {
        if (item.invertedCard !== undefined && window.confirm('Удалить перевёрнутую карточку? Это физическое удаление.')) {
            dispatch(quickAddActions.deleteGeneratedCard({itemId: item.id, direction: 'inverted', cardId: item.invertedCard.cardId}));
        }
    }, [dispatch, item.id, item.invertedCard]);

    return (
        <ItemRow theme={theme} $processing={item.status === 'processing'}>
            <ItemHeader>
                <ItemText theme={theme}>{item.text}</ItemText>
                <StatusBadge theme={theme} $status={item.status}>{statusLabel[item.status]}</StatusBadge>
                {item.status === 'done' && (
                    <LinkButton theme={theme} onClick={toggleHandler}>
                        {expanded ? 'Свернуть' : 'Развернуть'}
                    </LinkButton>
                )}
                {item.status === 'error' && (
                    <LinkButton theme={theme} onClick={retryHandler}>Повторить</LinkButton>
                )}
                <LinkButton theme={theme} onClick={deleteHandler}>✕</LinkButton>
            </ItemHeader>

            {item.status === 'error' && item.error !== undefined && (
                <ErrorText theme={theme}>{item.error}</ErrorText>
            )}

            {item.status === 'duplicate' && (
                <DuplicateNotice theme={theme}>
                    <span>Уже добавлено:</span>
                    {item.normalCard !== undefined && (
                        <DuplicateLink theme={theme} to={`/card/${item.normalCard.cardId}`}>Прямая</DuplicateLink>
                    )}
                    {item.invertedCard !== undefined && (
                        <DuplicateLink theme={theme} to={`/card/${item.invertedCard.cardId}`}>Перевёрнутая</DuplicateLink>
                    )}
                </DuplicateNotice>
            )}

            {item.status === 'done' && expanded && (
                <ExpandedArea theme={theme}>
                    <Toggle theme={theme}>
                        <ToggleButton theme={theme} $active={direction === 'normal'} onClick={showNormal}>
                            Прямая
                        </ToggleButton>
                        <ToggleButton theme={theme} $active={direction === 'inverted'} onClick={showInverted}>
                            Перевёрнутая
                        </ToggleButton>
                    </Toggle>
                    {direction === 'normal' && item.normalCard !== undefined && (
                        <GeneratedCardView title="Прямая карточка" card={item.normalCard} onDelete={deleteNormalCardHandler} />
                    )}
                    {direction === 'normal' && item.normalCard === undefined && (
                        <EmptyCardNotice theme={theme}>Прямая карточка удалена</EmptyCardNotice>
                    )}
                    {direction === 'inverted' && item.invertedCard !== undefined && (
                        <GeneratedCardView title="Перевёрнутая карточка" card={item.invertedCard} onDelete={deleteInvertedCardHandler} />
                    )}
                    {direction === 'inverted' && item.invertedCard === undefined && (
                        <EmptyCardNotice theme={theme}>Перевёрнутая карточка удалена</EmptyCardNotice>
                    )}
                </ExpandedArea>
            )}
        </ItemRow>
    );
});
QuickAddItemRow.displayName = 'QuickAddItemRow';

const QuickAddComponent: React.FC<NavigationProps> = memo(() => {
    usePageTitle('Quick Add · Memkeep');
    const { items, inputText, expandedIds } = useSelector(selector);
    const dispatch = useDispatch();
    const { theme } = useTheme();
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        dispatch(quickAddActions.loadQueue());
    }, [dispatch]);

    const openMenuHandler = useCallback(() => {
        dispatch(uiActions.openSideMenu());
    }, [dispatch]);

    const changeHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(quickAddActions.setInputText(event.target.value));
    }, [dispatch]);

    const submitHandler = useCallback(() => {
        dispatch(quickAddActions.submit());
        inputRef.current?.focus();
    }, [dispatch]);

    const clearHandler = useCallback(() => {
        dispatch(quickAddActions.clearFinished());
    }, [dispatch]);

    const hasFinished = items.some(item => item.status === 'done' || item.status === 'error' || item.status === 'duplicate');

    const keyDownHandler = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submitHandler();
        }
    }, [submitHandler]);

    return (
        <Root>
            <ActionBar theme={theme}>
                <Icon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" onClick={openMenuHandler}>
                    <path d="M0 0h24v24H0z" fill="none"/>
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </Icon>
            </ActionBar>
            <Content theme={theme}>
                <InputRow>
                    <TextInput
                        ref={inputRef}
                        theme={theme}
                        autoFocus
                        placeholder="Слово или выражение…"
                        value={inputText}
                        onChange={changeHandler}
                        onKeyDown={keyDownHandler}
                    />
                    <Button theme={theme} onClick={submitHandler} disabled={inputText.trim() === ''}>
                        Add
                    </Button>
                </InputRow>

                {hasFinished && (
                    <QueueHeader>
                        <ClearButton theme={theme} onClick={clearHandler}>Очистить готовые</ClearButton>
                    </QueueHeader>
                )}

                <Queue>
                    {items.map(item => (
                        <QuickAddItemRow
                            key={item.id}
                            item={item}
                            expanded={expandedIds.includes(item.id)}
                        />
                    ))}
                </Queue>
            </Content>
            <SideMenu />
        </Root>
    );
});

QuickAddComponent.displayName = 'QuickAddComponent';

export { QuickAddComponent as QuickAdd };
